import React, {
  useMemo,
  useRef,
  useState,
  useCallback,
  useEffect,
} from "react";
import NexusGrid from "../../core/NexusGrid";
import { runQuery, upsertRegionRow } from "../../../db/alasqlInit";

const Regions = ({ questionnaireData, setQuestionnaireData }) => {
  const initialSqlQuery = "SELECT id, name FROM regions";
  const [sqlQuery, setSqlQuery] = useState(initialSqlQuery);
  // Regions/Countries: rows and columns for NexusGrid
  const [regionsRows, setRegionsRows] = useState(() => {
    const saved = questionnaireData?.foundationalSetup?.regionsHierarchy;
    if (Array.isArray(saved) && saved.length > 0) {
      return saved.map((r, idx) => ({
        id: r.id || `rg_${idx}_${Date.now()}`,
        ...r,
      }));
    }
    // Start empty; we'll try to load from alasql on mount
    return [];
  });

  // Single function to execute SQL and apply results
  const executeAndApplyQuery = useCallback(
    async (query) => {
      try {
        const { results, error } = await runQuery(query);
        if (error) throw error;
        if (!Array.isArray(results)) return;
        const mapped = results.map((r, idx) => ({
          id: r.id ? `rg_${r.id}` : `rg_${idx}_${Date.now()}`,
          hierarchyId: r.id,
          hierarchyName: r.name,
        }));
        setRegionsRows(mapped);
        setQuestionnaireData((prev) => ({
          ...prev,
          foundationalSetup: {
            ...prev.foundationalSetup,
            regionsHierarchy: mapped,
          },
        }));
        // Sync alasql regions table from applied results
        mapped.forEach((m) => {
          const id = m.hierarchyId;
          const name = m.hierarchyName;
          if (!id) return;
          const country = id.startsWith("us-")
            ? "USA"
            : id.startsWith("ca-")
            ? "Canada"
            : "";
          upsertRegionRow({ id, name, country });
        });
      } catch (e) {
        console.error("SQL error executing query", e);
      }
    },
    [setQuestionnaireData]
  );

  // On mount, if nothing persisted, run the initial SQL once
  useEffect(() => {
    const saved = questionnaireData?.foundationalSetup?.regionsHierarchy;
    if (Array.isArray(saved) && saved.length > 0) return; // already loaded
    executeAndApplyQuery(initialSqlQuery);
    setSqlQuery(initialSqlQuery);
  }, [questionnaireData, executeAndApplyQuery]);

  const regionsColumnDefs = useMemo(
    () => [
      {
        headerName: "Hierarchy ID",
        field: "hierarchyId",
        editable: true,
        width: 200,
      },
      {
        headerName: "Hierarchy Name",
        field: "hierarchyName",
        editable: true,
        flex: 1,
      },
    ],
    []
  );

  // Grid behaviors similar to Locations: coloring and actions context
  const regionsGridRef = useRef(null);

  const getRegionsRowStyle = useCallback((params) => {
    const s = params.data?.__status;
    if (s === "new") return { backgroundColor: "#f0fdf4" }; // green-50
    if (s === "deleted") return { backgroundColor: "#fef2f2" }; // red-50
    if (s === "merged") return { backgroundColor: "#fffbeb" }; // amber-50
    return undefined;
  }, []);

  const refreshRegionsRowById = useCallback((id) => {
    const api = regionsGridRef.current?.api;
    if (!api) return;
    const rowNode = id ? api.getRowNode(id) : null;
    if (rowNode) {
      api.refreshCells({
        rowNodes: [rowNode],
        force: true,
        columns: ["actions"],
      });
    }
    // ensure Row # column updates for all rows
    api.refreshCells({ force: true, columns: ["rowNumber"] });
  }, []);

  const handleAddRegion = () => {
    const newRow = {
      id: `rg_${Math.random().toString(36).slice(2)}_${Date.now().toString(
        36
      )}`,
      hierarchyId: "",
      hierarchyName: "",
      __status: "new",
    };
    setRegionsRows((prev) => [newRow, ...prev]);
    setQuestionnaireData((prev) => ({
      ...prev,
      foundationalSetup: {
        ...prev.foundationalSetup,
        regionsHierarchy: [
          newRow,
          ...(prev?.foundationalSetup?.regionsHierarchy || []),
        ],
      },
    }));
  };

  const handOnSqlApply = async (query) => {
    setSqlQuery(query);
    executeAndApplyQuery(query);
  };

  const onRegionsCellValueChanged = (params) => {
    const id = params.data?.id;
    const field = params.colDef?.field;
    const value = params.newValue;
    if (!id || !field) return;
    setRegionsRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
    setQuestionnaireData((prev) => ({
      ...prev,
      foundationalSetup: {
        ...prev.foundationalSetup,
        regionsHierarchy: (prev?.foundationalSetup?.regionsHierarchy || []).map(
          (r) => (r.id === id ? { ...r, [field]: value } : r)
        ),
      },
    }));

    // Upsert to alasql when we have both id and name
    const row = params.data;
    const hierarchyId = field === "hierarchyId" ? value : row?.hierarchyId;
    const hierarchyName =
      field === "hierarchyName" ? value : row?.hierarchyName;
    if (hierarchyId) {
      const country = hierarchyId.startsWith("us-")
        ? "USA"
        : hierarchyId.startsWith("ca-")
        ? "Canada"
        : "";
      upsertRegionRow({ id: hierarchyId, name: hierarchyName || "", country });
    }
  };

  // Provide a setRowData-like API to ActionsRenderer via grid context
  const setRegionsDataForContext = useCallback(
    (updater) => {
      setRegionsRows((prev) => {
        const next = updater(prev);
        // persist
        setQuestionnaireData((prevQ) => ({
          ...prevQ,
          foundationalSetup: {
            ...prevQ.foundationalSetup,
            regionsHierarchy: next,
          },
        }));
        // defer refresh so Grid sees the updated data
        // caller will call refreshRowById with id
        return next;
      });
    },
    [setQuestionnaireData]
  );

  return (
    <NexusGrid
      key="regions"
      title="Regions/Countries"
      actionText="Add Region"
      onAdd={handleAddRegion}
      columnDefs={regionsColumnDefs}
      rowData={regionsRows}
      sqlInitialQuery={sqlQuery}
      onSqlApply={handOnSqlApply}
      showRowNumbers={true}
      showActions={true}
      onCellValueChanged={onRegionsCellValueChanged}
      getRowId={(p) => p.data.id}
      pagination={false}
      containerClassName="border border-gray-200 rounded-lg bg-white shadow-sm mb-6"
      gridRef={regionsGridRef}
      rowHeight={36}
      getRowStyle={getRegionsRowStyle}
      context={{
        setRowData: setRegionsDataForContext,
        refreshRowById: refreshRegionsRowById,
      }}
    />
  );
};

export default Regions;
